/**
 * FeaturedBackgroundVideo Template Script
 * ------------------------------------------------------------------------------
 * A file that contains scripts highly couple code to the FeaturedBackgroundVideo template.
 *
 * @namespace FeaturedBackgroundVideo
 */

import {register} from './../vendor/theme-scripts/theme-sections';
import debounce from '../util/debounce';
import throttle from '../util/throttle';

const sections = {};

const selectors = {
  shopifySection: '.shopify-section',
  headerContent: '.header__content',
  heroContent: '.hero__content',
  heroContentWrapper: '.hero__content__wrapper',
  checkTrueString: 'true',
  dataSectionId: 'data-section-id',
  dataEnableSound: 'data-enable-sound',
  dataVideoId: 'data-video-id',
  dataVideoType: 'data-video-type',
  attrId: 'id',
  loaded: 'loaded',
  videoIframe: '.video__iframe',
  videoWrapper: '.video-wrapper',
  youtubeWrapper: '[data-youtube-wrapper]',
};

const players = [];
let isYoutubeAPIReady = false;
const defaultYoutubeOptions = {
  height: '720',
  width: '1280',
  playerVars: {
    cc_load_policy: 0,
    iv_load_policy: 3,
    modestbranding: 1,
    playsinline: 1,
    autohide: 0,
    controls: 0,
    branding: 0,
    showinfo: 0,
    rel: 0,
    fs: 0,
    wmode: 'opaque',
  },
  events: {
    onReady: onPlayerReady,
    onStateChange: onPlayerStateChange,
  },
};

function onPlayerReady(event) {
  const eventIframe = event.target.getIframe();
  const id = eventIframe.getAttribute(selectors.attrId);
  const enableSound = document.querySelector(`#${id}`).getAttribute(selectors.dataEnableSound) === selectors.checkTrueString;

  eventIframe.setAttribute('tabindex', '-1');

  if (enableSound) {
    event.target.unMute();
  } else {
    event.target.mute();
  }
  event.target.playVideo();

  checkPlayerVisibility(id);

  window.addEventListener(
    'scroll',
    throttle(() => {
      checkPlayerVisibility(id);
    }, 150)
  );
}

function onPlayerStateChange(event) {
  // Loop video if state is ended
  if (event.data == 0) {
    event.target.playVideo();
  }
  if (event.data == 1) {
    // video is playing
    event.target.getIframe().parentElement.classList.add(selectors.loaded);
  }
}

function checkPlayerVisibility(id) {
  let playerID;
  if (typeof id === 'string') {
    playerID = id;
  } else if (id.data != undefined) {
    playerID = id.data.id;
  } else {
    return false;
  }

  const playerElement = document.getElementById(playerID + '-container');
  const player = players[playerID];
  const box = playerElement.getBoundingClientRect();
  let isVisible = visibilityHelper.isElementPartiallyVisible(playerElement) || visibilityHelper.isElementTotallyVisible(playerElement);

  // Fix the issue when element height is bigger than the viewport height
  if (box.top < 0 && playerElement.clientHeight + box.top >= 0) {
    isVisible = true;
  }

  if (isVisible && player && typeof player.playVideo === 'function') {
    player.playVideo();
  } else if (!isVisible && player && typeof player.pauseVideo === 'function') {
    player.pauseVideo();
  }
}

class FeaturedBackgroundVideo {
  /**
   * FeaturedBackgroundVideo section constructor. Runs on page load as well as Theme Editor
   * `section:load` events.
   * @param {string} container - selector for the section container DOM element
   */

  constructor(section) {
    const self = this;
    this.container = section.container;
    this.parentContainer = this.container.closest(selectors.shopifySection);
    this.parentContainerIdx = Array.from(this.parentContainer.parentElement.children).indexOf(this.parentContainer);
    const player = (this.player = this.container.querySelector(selectors.videoIframe));

    this.setMinHeight();

    window.addEventListener('resize', debounce(self.setMinHeight(), 250));

    if (player) {
      this.playerID = player.querySelector(selectors.youtubeWrapper).getAttribute(selectors.attrId);
      this.videoID = player.getAttribute(selectors.dataVideoId);
      this.videoType = player.getAttribute(selectors.dataVideoType);
      this.init();
    }

    document.addEventListener('touchstart', function (e) {
      if (e.target.matches(selectors.videoWrapper) || e.target.closest(selectors.videoWrapper)) {
        const playerID = e.target.querySelector(selectors.videoIframe).getAttribute(selectors.attrId);
        players[playerID].playVideo();
      }
    });
  }

  init() {
    if (this.videoType == 'youtube') {
      if (isYoutubeAPIReady) {
        this.loadYoutubePlayer();
      } else {
        // Load Youtube API if not loaded yet
        window.loadYoutubeAPI();
        document.body.addEventListener('youtubeAPIReady', this.loadYoutubePlayer.bind(this));
      }
    } else if (this.videoType == 'vimeo') {
      const oembed_url = 'https://vimeo.com/api/oembed.json';
      const vimeo_url = 'https://vimeo.com/' + this.videoID;
      let paramsString = '';
      const state = this;

      const params = {
        url: vimeo_url,
        background: true,
        muted: true,
        autoplay: true,
      };

      for (let key in params) {
        paramsString += encodeURIComponent(key) + '=' + encodeURIComponent(params[key]) + '&';
      }

      fetch(`${oembed_url}?${paramsString}`)
        .then((response) => response.json())
        .then(function (data) {
          state.player.innerHTML = data.html;

          setTimeout(function () {
            state.player.parentElement.classList.add(selectors.loaded);
          }, 1000);
        })
        .catch(function () {
          console.log('error');
        });
    }
  }

  setMinHeight() {
    const isFirstSection = this.parentContainerIdx == 0;
    const windowWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
    const headerContent = document.querySelector(selectors.headerContent);
    let headerContentHeight = 0;
    if (headerContent) {
      headerContentHeight = parseFloat(getComputedStyle(headerContent, null).height.replace('px', ''));
    }

    let headerHeight = windowWidth >= 750 ? headerContentHeight : 0;
    if (!isFirstSection) headerHeight = 0;
    const contentHeight = this.container.querySelector(selectors.heroContent).offsetHeight;
    const minHeight = headerHeight + contentHeight;

    this.container.style.minHeight = `${minHeight}px`;
    this.container.querySelector(selectors.heroContentWrapper).style.paddingTop = `${headerHeight}px`;
  }

  loadYoutubePlayer() {
    const currentYoutubeOptions = Object.assign({}, defaultYoutubeOptions);
    currentYoutubeOptions.videoId = this.videoID;
    if (this.videoID.length) {
      players[this.playerID] = new YT.Player(this.playerID, currentYoutubeOptions);
    }
    isYoutubeAPIReady = true;
  }

  onReorder() {
    this.setMinHeight();
  }

  onUnload(evt) {
    const playerID = 'youtube-' + this.container.getAttribute(selectors.dataSectionId);
    players[playerID].destroy();
  }
}

const FeaturedBackgroundVideoSection = {
  onLoad() {
    sections[this.id] = new FeaturedBackgroundVideo(this);
  },
  onUnload(e) {
    sections[this.id].onUnload(e);
  },
  onBlockSelect(e) {
    sections[this.id].onBlockSelect(e);
  },
};

register('featured-background-video', FeaturedBackgroundVideoSection);
